import React, { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Callout, Illustration } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { WalletType } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { WalletTypes } from 'types/wallets'
import { roundNumberWithCommas } from 'utils/utils'
import { formDataAtom, transferErrorAtom } from './atoms/atoms'
import { useSourceWallet } from './hooks/useSourceWallet'

export const MoveComplete = () => {
  const { t } = useTranslation()
  const sourceWallet = useSourceWallet()
  const navigate = useNavigate()
  const [walletAction] = useWalletActionContext()
  const queryClient = useQueryClient()
  const formValues = useAtomValue(formDataAtom)
  const transferError = useAtomValue(transferErrorAtom)
  const isCustomerWalletTransfer = formValues?.walletTransferType === WalletTransferType.CUSTOMER
  const isTransferFromCustomer =
    walletAction?.meta?.sourceWalletType === WalletTransferType?.CUSTOMER

  const { data: wallets, isLoading: isLoadingWallets } = useWalletsQueryV2({
    walletType: WalletTypes.FIAT,
    searchTerm: sourceWallet?.currency,
    max: 1000,
  })

  const destinationMainWallet = wallets?.data?.find((wallet: WalletType) => {
    return wallet?.lsid === formValues?.destinationWallet
  })

  const goToTransferDestination = () => {
    if (!isCustomerWalletTransfer && destinationMainWallet) {
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      navigate(
        `${PathNames.WALLETS}/wallet-details/${destinationMainWallet?.id}?tab=recent-activity`,
        {
          replace: true,
        }
      )
    } else if (isCustomerWalletTransfer && isTransferFromCustomer) {
      queryClient.invalidateQueries({ queryKey: ['customerWallets'] })
      walletAction?.onClose?.()
    } else {
      queryClient.invalidateQueries({ queryKey: ['customerWallets'] })
      navigate(`${PathNames.CUSTOMERS}/${formValues?.customerReference}`, {
        replace: true,
      })
    }
  }

  const labels = useMemo(() => {
    const destinationLabel = t('customers.moveSuccessful', {
      amount: roundNumberWithCommas(formValues?.amount),
      currency: sourceWallet?.currency,
      customer: isCustomerWalletTransfer
        ? formValues?.destinationCustomerName
        : formValues?.destinationWalletName,
    })

    const customerWalletLabel = isCustomerWalletTransfer ? formValues?.destinationWalletName : ''

    return {
      destinationLabel,
      customerWalletLabel,
    }
  }, [formValues])

  const moveCompleteButtonLabel = isCustomerWalletTransfer
    ? t('customers.goToCustomer')
    : t('customers.goToWallet')

  return (
    <Box flexCol align="center" className="my-6 w-full space-y-4">
      <Illustration name="HandsClapping" color="success" />
      <h1 className="text-foreground-prominent text-h1 text-center">{labels?.destinationLabel}</h1>
      {labels?.customerWalletLabel && (
        <p className="m-0 text-center text-foreground-prominent text-body-regular">
          {t('customers.customerWallet')}: {formValues?.destinationWalletName}
        </p>
      )}
      <Button variant="primary" onClick={goToTransferDestination} loading={isLoadingWallets}>
        {moveCompleteButtonLabel}
      </Button>
      {!!transferError && <Callout variant="error">{transferError}</Callout>}
    </Box>
  )
}
